import {Button, Card, Modal, Tabs, Tooltip} from "antd";
import BaseLayout from "../components/layout/layout-with-sider/BaseLayout";
import DecimalPlacesSettings from "../app-components/user-settings/DecimalPlacesSettings";
import ThresholdSettings from "../app-components/user-settings/ThresholdSettings";
import ApiService from "../services/ApiService";
import DefaultsSettings from "../app-components/user-settings/DefaultsSettings";
import {useUser} from "../context/UserContext";
import {ExclamationCircleFilled} from "@ant-design/icons";
import '../input-number.css'
import CalculationTolerancesSettings from "../app-components/user-settings/CalculationTolerancesSettings";
import WSELLibrary from "../app-components/user-settings/WSELLibrary";
import {AppActions, useAppContext} from "../context/AppContext";
import SurveySettings from "../app-components/user-settings/SurveySettings";
import ModelLayersSettings from "../app-components/user-settings/ModelLayersSettings";

export default function UserSettings() {
    const {user, setSettings} = useUser()
    const {dispatchProject} = useAppContext()

    const getValues = () => {
        let form1 = null
        let form2 = null
        let form3 = null
        let form4 = null

        form1 = fixNullValues(form1, user.defaultSettings.decimalPlaces, user.settings.decimalPlaces || {})
        form2 = fixNullValues(form2, user.defaultSettings.thresholds, user.settings.thresholds || {})
        form3 = fixNullValues(form3, user.defaultSettings.defaults, user.settings.defaults || {})
        form4 = fixNullValues(form4, user.defaultSettings.calculation_tolerances, user.settings.calculation_tolerances || {})

        return {
            decimalPlaces: form1,
            thresholds: form2,
            defaults: form3,
            others: user.settings.others || {},
            calculation_tolerances: form4,
            map_settings: user.settings.map_settings || {}
        }
    }

    const onSave = async ({decimalPlaces, thresholds, defaults, others, calculation_tolerances, map_settings}) => {
        const res = await ApiService.User.UpdateUserSettings(JSON.stringify({
            decimalPlaces, thresholds, defaults, others, calculation_tolerances, map_settings
        }))

        if(res.success) {
            dispatchProject({
                type: AppActions.SET_HISTORY,
                data: {history: []}
            })
            const res2 = await ApiService.User.UserSettings();
            setSettings({
                settings: JSON.parse(res2.data.settings),
                defaultSettings: res2.data.defaultSettings,
            })
        }
    }

    const onReset = async () => {
        Modal.confirm({
            title: 'Do you want to reset all settings?',
            icon: <ExclamationCircleFilled />,
            content: '',
            onOk() {
                dispatchProject({
                    type: AppActions.SET_HISTORY,
                    data: {history: []}
                })

                setSettings({
                    settings: JSON.parse(JSON.stringify(user.defaultSettings)),
                    defaultSettings: JSON.parse(JSON.stringify(user.defaultSettings)),
                })

                onSave({
                    ...user.defaultSettings
                })
            },
            onCancel() {},
        });
    }

    const fixNullValues = (formData, defaultValues, dbValues) => {
        if(!formData) {
            formData = {
                ...defaultValues,
                ...(dbValues ? dbValues : {})
            }
        }

        Object.keys(formData).forEach(x => {
            if(formData[x] === null || formData[x] === undefined)
                formData[x] = (dbValues[x] ?? defaultValues[x])
        })

        return formData
    }

    return (
        <>
            <BaseLayout>
                <Card title={'Settings'}>
                    <Tabs type={'card'} defaultActiveKey={'Thresholds'}>
                        <Tabs.TabPane key={'Thresholds'} tab={'Thresholds'}>
                            <ThresholdSettings
                                values={user?.settings?.thresholds || {}}
                                defValues={user?.defaultSettings?.thresholds || {}}
                                onValuesChange={(key, value) => {
                                    user.settings.thresholds[key] = value
                                    setSettings({
                                        settings: user.settings,
                                        defaultSettings: user.defaultSettings
                                    })
                                }}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane key={'Defaults'} tab={'Defaults'}>
                            <DefaultsSettings
                                values={user?.settings?.defaults || {}}
                                defValues={user?.defaultSettings?.defaults || {}}
                                onValuesChange={(key, value) => {
                                    user.settings.defaults[key] = value
                                    setSettings({
                                        settings: user.settings,
                                        defaultSettings: user.defaultSettings
                                    })
                                }}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane key={'Decimal Places'} tab={'Decimal Places'}>
                            <DecimalPlacesSettings
                                values={user?.settings?.decimalPlaces || {}}
                                defValues={user?.defaultSettings?.decimalPlaces || {}}
                                onValuesChange={(key, value) => {
                                    user.settings.decimalPlaces[key] = value
                                    setSettings({
                                        settings: user.settings,
                                        defaultSettings: user.defaultSettings
                                    })
                                }}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane key={'Calculation Tolerances'} tab={'HEC-RAS Calculation Tolerances'}>
                            <CalculationTolerancesSettings
                                values={user?.settings?.calculation_tolerances || {}}
                                defValues={user?.defaultSettings?.calculation_tolerances || {}}
                                onValuesChange={(key, value) => {
                                    if(!user.settings?.calculation_tolerances)
                                        user.settings.calculation_tolerances = {}

                                    user.settings.calculation_tolerances[key] = value
                                    setSettings({
                                        settings: user.settings,
                                        defaultSettings: user.defaultSettings
                                    })
                                }}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane key={'WSEL Library'} tab={'WSEL Library'}>
                            <WSELLibrary
                                values={user?.settings?.defaults || {}}
                                defValues={user?.defaultSettings?.defaults || {}}
                                onValuesChange={(key, value) => {
                                    if(!user?.settings?.defaults)
                                        user.settings.defaults = {}

                                    user.settings.defaults[key] = value
                                    setSettings({
                                        settings: user.settings,
                                        defaultSettings: user.defaultSettings
                                    })
                                }}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane key={'Survey'} tab={'Survey'}>
                            <SurveySettings
                                values={user?.settings?.defaults || {}}
                                defValues={user?.defaultSettings?.defaults || {}}
                                onValuesChange={(key, value) => {
                                    if(!user?.settings?.defaults)
                                        user.settings.defaults = {}

                                    user.settings.defaults[key] = value
                                    setSettings({
                                        settings: user.settings,
                                        defaultSettings: user.defaultSettings
                                    })
                                }}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane key={'Model Layers'} tab={'Model Layers'}>
                            <ModelLayersSettings
                                values={user?.settings?.map_settings || {}}
                                defValues={user?.defaultSettings?.map_settings || {}}
                                onValuesChange={(key, value) => {
                                    if(!user?.settings?.map_settings)
                                        user.settings.map_settings = {}

                                    user.settings.map_settings[key] = value
                                    setSettings({
                                        settings: user.settings,
                                        defaultSettings: user.defaultSettings
                                    })
                                }}
                            />
                        </Tabs.TabPane>
                    </Tabs>
                    <br/>
                    <div style={{display: 'flex', gap: 8}}>
                        <Tooltip title={`Resets the User Preference values to System Default values in Thresholds, Defaults, and Decimal Places settings.`}>
                            <Button
                                size={'small'}
                                danger={true}
                                onClick={onReset}
                            >
                                Reset
                            </Button>
                        </Tooltip>
                        <Tooltip title={`Save the user default values for ${user.data.name} in Thresholds, Defaults and Decimal Places settings`}>
                            <Button
                                size={'small'}
                                type={'primary'}
                                onClick={async () => {
                                    const values = getValues();
                                    await onSave(values)
                                }}
                            >
                                Save
                            </Button>
                        </Tooltip>
                    </div>
                </Card>
            </BaseLayout>
        </>
    )
}